import { useState, useEffect } from 'react'
import { Modal, Form, Select, message } from 'antd'
import { PROJECT_REVIEW_HEALTH_API_URL } from '../../config/constants'
import { apiFetch } from '../../utils/apiClient'







export function CreateProjectReviewModal({ open, onClose, onSuccess }) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [projects, setProjects] = useState([])
  const [error, setError] = useState('')

  const getAuthHeader = () => {
    const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken') || sessionStorage.getItem('accessToken')
    if (!token) return undefined
    const normalizedToken = token.replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '').trim()
    if (!normalizedToken) return undefined
    return `Bearer ${normalizedToken}`
  }

  // Fetch all projects for the dropdown
  useEffect(() => {
    if (!open) return

    const fetchProjects = async () => {
      setProjectsLoading(true)
      try {
        const requestUrl = new URL(PROJECT_REVIEW_HEALTH_API_URL, globalThis.location.origin)
        requestUrl.searchParams.set('page', '0')
        requestUrl.searchParams.set('size', '1000') // Fetch all projects
        requestUrl.searchParams.set('sortBy', 'projectId')

        const authHeader = getAuthHeader()
        const response = await apiFetch(requestUrl.toString(), {
          method: 'GET',
          headers: {
            ...(authHeader ? { Authorization: authHeader } : {}),
            Accept: 'application/json',
          },
        }, 'Failed to fetch projects')

        if (!response.ok) {
          throw new Error('Failed to fetch projects')
        }

        const payload = await response.json()
        const projectsList = Array.isArray(payload.projects) ? payload.projects : (Array.isArray(payload.content) ? payload.content : [])
        
        const projectOptions = projectsList.map((p) => ({
          id: String(p.projectId || ''),
          name: String(p.projectName || ''),
        }))
        
        setProjects(projectOptions)
      } catch (err) {
        console.error('Error fetching projects:', err)
        message.error('Failed to load projects')
      } finally {
        setProjectsLoading(false)
      }
    }

    fetchProjects()
  }, [open])

  const handleSubmit = async () => {
    try {
      setError('')
      const values = await form.validateFields()
      setLoading(true)

      const payload = {
        projectId: values.projectId,
        pmoRating: values.pmoRating ? Number(values.pmoRating) : undefined,
        pmRating: values.pmRating ? Number(values.pmRating) : undefined,
        scope: values.scope || undefined,
        schedule: values.schedule || undefined,
        quality: values.quality || undefined,
        riskIssue: values.riskIssue || undefined,
        resource: values.resource || undefined,
        invoiceStatus: values.invoiceStatus || undefined,
      }

      const authHeader = getAuthHeader()
      const response = await apiFetch(
        `https://techsolutionsservice.ustdev.com/api/track-on/projectReview/${values.projectId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(authHeader ? { Authorization: authHeader } : {}),
          },
          body: JSON.stringify(payload),
        },
        'Failed to create project review'
      )

      if (!response.ok) {
        throw new Error('Failed to create project review')
      }

      message.success('Project review created successfully')
      form.resetFields()
      onClose()
      onSuccess?.()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred'
      setError(errorMsg)
      message.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setError('')
    onClose()
  }

  const ragOptions = [
    { value: 'G', label: '🟢 On Track' },
    { value: 'A', label: '🟡 Needs Attention' },
    { value: 'R', label: '🔴 At Risk' },
    { value: 'B', label: '⚫ Not Updated' },
    { value: 'N', label: '⚫ Not Updated' },
  ]

  const ratingOptions = [
    { value: '1', label: '1 - Black' },
    { value: '2', label: '2 - Red' },
    { value: '3', label: '3 - Amber' },
    { value: '4', label: '4 - Green' },
  ]

  return (
    <Modal
      title="Create Project Review"
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="Create"
      cancelText="Cancel"
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        {error && (
          <div style={{ marginBottom: 16, padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, color: '#991b1b', fontSize: 12 }}>
            {error}
          </div>
        )}

        {/* Project ID - Mandatory */}
        <Form.Item
          name="projectId"
          label="Project ID"
          rules={[{ required: true, message: 'Please select a project' }]}
        >
          <Select
            placeholder="Select project..."
            loading={projectsLoading}
            options={projects.map(p => ({
              value: p.id,
              label: `${p.id} – ${p.name}`,
            }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        {/* PMO Rating */}
        <Form.Item
          name="pmoRating"
          label="PMO Rating"
        >
          <Select
            placeholder="Select PMO rating..."
            options={ratingOptions}
            allowClear
          />
        </Form.Item>

        {/* PM Rating */}
        <Form.Item
          name="pmRating"
          label="PM Rating"
        >
          <Select
            placeholder="Select PM rating..."
            options={ratingOptions}
            allowClear
          />
        </Form.Item>

        {/* Status Fields Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item
            name="scope"
            label="Scope"
          >
            <Select
              placeholder="Select..."
              options={ragOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="schedule"
            label="Schedule"
          >
            <Select
              placeholder="Select..."
              options={ragOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="quality"
            label="Quality"
          >
            <Select
              placeholder="Select..."
              options={ragOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="riskIssue"
            label="Risk / Issue"
          >
            <Select
              placeholder="Select..."
              options={ragOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="resource"
            label="Resource"
          >
            <Select
              placeholder="Select..."
              options={ragOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="invoiceStatus"
            label="Invoice"
          >
            <Select
              placeholder="Select..."
              options={ragOptions}
              allowClear
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  )
}
